import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["identifier", "__isNew__"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { filter, find, has, isArray, isEmpty, isObject, pluck, sortBy } from 'underscore';
import PropTypes from 'prop-types';
import moment from 'moment';
import React from 'react';
import MultiPicker from '../../common/controls/multiPicker';
import { leafPedigreesString } from '../../../../behaviors/src/lib/common';
import { removeNulls } from '../../../utils/contentTools';
import localChangeSelector from '../selectors/localChangeSelector';

/**
 * Note that this is not a connected component. optionPickerField is connected and it's the
 * only code that creates OptionMultiPicker
 */
/* eslint-disable no-underscore-dangle */
const OptionMultiPicker = _ref => {
  let _ref$allowsOther = _ref.allowsOther,
    allowsOther = _ref$allowsOther === void 0 ? false : _ref$allowsOther,
    _ref$allowsMultipleSe = _ref.allowsMultipleSelection,
    allowsMultipleSelection = _ref$allowsMultipleSe === void 0 ? true : _ref$allowsMultipleSe,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    contentPath = _ref.contentPath,
    enabled = _ref.enabled,
    _ref$labelKey = _ref.labelKey,
    labelKey = _ref$labelKey === void 0 ? 'title' : _ref$labelKey,
    options = _ref.options,
    _ref$valueKey = _ref.valueKey,
    valueKey = _ref$valueKey === void 0 ? 'identifier' : _ref$valueKey,
    _ref$values = _ref.values,
    values = _ref$values === void 0 ? '' : _ref$values,
    path = _ref.path,
    onChange = _ref.onChange,
    _ref$onTextChange = _ref.onTextChange,
    onTextChange = _ref$onTextChange === void 0 ? null : _ref$onTextChange,
    _ref$endMessage = _ref.endMessage,
    endMessage = _ref$endMessage === void 0 ? null : _ref$endMessage,
    _ref$testingIdentifie = _ref.testingIdentifier,
    testingIdentifier = _ref$testingIdentifie === void 0 ? '' : _ref$testingIdentifie;
  const localChangeFunc = localChangeSelector({}, {
    allowsOther,
    allowsMultipleSelection,
    contentPath,
    className,
    enabled,
    labelKey,
    options,
    valueKey,
    values,
    path,
    onChange,
    testingIdentifier
  });
  const handleChange = function handleChange() {
    let incomingValues = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    if (incomingValues.length === 0) {
      return onChange(localChangeFunc({
        path,
        value: null
      }));
    }
    const changes = {};
    incomingValues.forEach(value => {
      const identifier = value.identifier,
        __isNew__ = value.__isNew__,
        rest = _objectWithoutProperties(value, _excluded);
      if (!rest._created) {
        rest._created = new Date().toISOString();
      }
      changes[identifier] = rest;
    });
    if (Object.keys(changes).length === 0) {
      return onChange(localChangeFunc({
        path,
        value: null
      }));
    }
    // removeNulls mutates value...
    const c = JSON.parse(JSON.stringify(changes));
    const displayValue = leafPedigreesString(removeNulls(c));
    return onChange(localChangeFunc({
      path,
      displayValue,
      value: changes
    }));
  };

  /**
   *  @function prepareValues - transforms data coming from server.
   */
  const prepareValues = () => {
    let transformedValues = values;
    let ids = [];
    if (isObject(values) && isEmpty(values)) {
      transformedValues = [];
    }
    if (isObject(values) && Object.keys(values).length) {
      transformedValues = [];
      Object.keys(values).forEach(key => {
        if (values[key] !== null) {
          // do not display items with null values.
          transformedValues.push({
            [labelKey]: values[key][labelKey],
            [valueKey]: key,
            _created: values[key]._created
          });
        }
      });
      transformedValues = sortBy(transformedValues, o => {
        const x = moment(o._created, 'x');
        return x.isValid() ? x : o._created;
      });
      ids = pluck(transformedValues, valueKey);
    }
    return {
      ids,
      values: transformedValues
    };
  };

  /**
   * @function prepareOptions - sorts options by _created field;
   *
   */
  const prepareOptions = () => {
    // check options array for existence of valueKey and labelKey fields.
    // if the valueKey field (ex. identifier) is missing,
    // create a valueKey field and assign it a value derived from labelKey.
    //
    // Also add a _created property to each option
    // based its order in the options array.
    const rawOptions = JSON.parse(JSON.stringify(options));
    let newOption = {};
    const sortedOptions = [];
    rawOptions.forEach((option, index) => {
      newOption = option;
      if (!has(newOption, '_created')) {
        newOption._created = index + 1;
      }
      if (!has(newOption, valueKey)) {
        newOption[valueKey] = "_".concat(newOption[labelKey]);
      }
      sortedOptions.push(newOption);
    });
    return sortedOptions;
  };

  /**
   * @function prepareOtherOptions - adds an "other" value to the
   *                      options list so it may be shown as selected.
   *                      assign it a _created property equal to utc date.
   */
  const prepareOtherOptions = () => {
    let optionsWithOther = prepareOptions();
    const preparedValues = prepareValues(values);
    const otherValues = preparedValues.values;

    // handles adding in "other" value to the options list
    if (isArray(otherValues) && otherValues.length > 0) {
      // include the value if it is NOT found in the option list.
      const valuesNotInOptions = filter(otherValues, val => !find(optionsWithOther, option => option[valueKey] === val[valueKey]));
      valuesNotInOptions.forEach(value => {
        optionsWithOther = optionsWithOther.concat({
          [valueKey]: value[valueKey],
          [labelKey]: value[labelKey],
          _created: value._created ? value._created : moment().toISOString()
        });
      });
    }
    return optionsWithOther;
  };
  const newProps = _objectSpread(_objectSpread({}, {
    allowsOther,
    allowsMultipleSelection,
    contentPath,
    className,
    enabled,
    labelKey,
    options,
    valueKey,
    values,
    path,
    onChange,
    onTextChange,
    endMessage,
    testingIdentifier
  }), {}, {
    values: prepareValues().ids,
    multi: true,
    onChange: handleChange,
    disabled: !enabled,
    verticalPadding: false
  });
  if (allowsOther) {
    newProps.allowCreate = true;
    newProps.options = prepareOtherOptions();
    newProps.className += testingIdentifier !== '' ? " t-omp t-omp-".concat(testingIdentifier, " ") : '';
  } else {
    newProps.options = prepareOptions();
    newProps.className += testingIdentifier !== '' ? " t-omp t-omp-".concat(testingIdentifier, " ") : '';
  }

  /* eslint-disable-next-line react/jsx-props-no-spreading */
  return /*#__PURE__*/React.createElement(MultiPicker, newProps);
};
OptionMultiPicker.propTypes = {
  allowsMultipleSelection: PropTypes.bool,
  allowsOther: PropTypes.bool,
  className: PropTypes.string,
  contentPath: PropTypes.string.isRequired,
  enabled: PropTypes.bool.isRequired,
  path: PropTypes.string.isRequired,
  labelKey: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onTextChange: PropTypes.func,
  endMessage: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  testingIdentifier: PropTypes.string,
  valueKey: PropTypes.string,
  values: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({}), PropTypes.array])
};
export default OptionMultiPicker;